// Thin fetch wrapper: same-origin cookie session + CSRF header, problem+json errors.

export const BASE = import.meta.env.BASE_URL.replace(/\/$/, ''); // "/bakery"
const API = `${BASE}/api/v1`;

let csrfToken: string | null = null;
export const setCsrf = (t: string | null) => {
  csrfToken = t;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public body: any,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
  const h: Record<string, string> = { accept: 'application/json', ...headers };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    h['content-type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  if (method !== 'GET' && csrfToken) h['x-csrf-token'] = csrfToken;
  const res = await fetch(`${API}${path}`, { method, headers: h, body: payload, credentials: 'same-origin' });
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  const data = ct.includes('json') ? await res.json() : await res.text();
  if (!res.ok) {
    const code = typeof data === 'object' && data?.type ? String(data.type).split('/').pop()! : 'error';
    throw new ApiError(res.status, code, (typeof data === 'object' && data?.detail) || res.statusText, data);
  }
  return data as T;
}

export const api = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b?: unknown, h?: Record<string, string>) => request<T>('POST', p, b ?? {}, h),
  patch: <T>(p: string, b: unknown, h?: Record<string, string>) => request<T>('PATCH', p, b, h),
  del: <T>(p: string) => request<T>('DELETE', p),
  upload: <T>(p: string, form: FormData) => request<T>('POST', p, form),
};

/** Ends the Bakery session, then signs out at Identity DHM too (single logout). */
export async function logout() {
  const r = await api.post<{ redirect: string }>('/auth/logout');
  setCsrf(null);
  location.href = r.redirect || `${BASE}/`;
}

export function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    if (Array.isArray(v)) {
      if (v.length) u.set(k, v.join(','));
    } else u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

// ---------------------------------------------------------------- shared types (mirror the server's output)

export interface MediaRef {
  id: string;
  url: string;
  thumb_url: string;
  alt_text: string | null;
}

export interface CardSummary {
  id: string;
  type: string;
  title: string;
  slug: string;
  summary: string | null;
  status: string;
  favourite: boolean;
  rating: number | null;
  needs_review: boolean;
  tags: string[];
  cover: MediaRef | null;
  face: { total_min: number | null; servings: string | null };
  version: number;
  updated_at: string;
  last_used_at: string | null;
  deleted_at: string | null;
  url: string;
  snippet?: string;
}

export interface Card extends Omit<CardSummary, 'face' | 'snippet'> {
  schema_version: number;
  body_md: string | null;
  data: Record<string, any>;
  source_url: string | null;
  source_note: string | null;
  cover_media_id: string | null;
  created_by: string;
  updated_by: string;
  created_at: string;
  links: { id: string; title: string; type: string; relation: string; direction: 'out' | 'in' }[];
  media: (MediaRef & { role: string })[];
  collections?: { id: string; name: string }[];
}

export interface Page<T> {
  items: T[];
  next_cursor: string | null;
  total: number;
}

export interface CardType {
  key: string;
  label: string;
  schema_version: number;
  ui_hints: { icon?: string; face_fields?: string[] };
  json_schema: any;
}

export interface Me {
  authenticated: boolean;
  name: string | null;
  email: string | null;
  csrf_token: string | null;
  login_url: string;
  login_available: boolean;
  account_url: string | null;
  mcp_enabled: boolean;
}
